import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EPDStateModel } from 'services/EpdClient';
import { STATUS_COMMENT_MAX_LENGTH } from 'constants/constants';
import StatusCommentFullViewDialog from 'components/v2/dialogs/StatusCommentFullViewDialog';
import styled from 'styled-components';

type TProps = {
  epdState: EPDStateModel | undefined | null;
};

const StatusBox = ({ epdState }: TProps) => {
  const [showStatusCommentFullViewDialog, setShowStatusCommentFullViewDialog] = useState(false);

  const { t } = useTranslation();
  const isEPDDepublishedByAdminOrDeclined =
    epdState?.statusMessageCode === 'declinedVerification' ||
    epdState?.statusMessageCode === 'depublishedEPDNotPaid' ||
    epdState?.statusMessageCode === 'depublishedEPDNonCompliant' ||
    epdState?.statusMessageCode === 'declinedPublication' ||
    epdState?.statusMessageCode === 'oldWizardDeclinedPublicationProcessCertification';

  const statusMessage = t(`epdWizard.epdState.statusMessage.${epdState?.statusMessageCode}`);
  const actionMessage = t(`epdWizard.epdState.actionMessage.${epdState?.statusMessageCode}`);

  const commentToStatus =
    epdState?.commentToStatus &&
    (epdState.commentToStatus.length > STATUS_COMMENT_MAX_LENGTH
      ? epdState.commentToStatus.slice(0, STATUS_COMMENT_MAX_LENGTH) + '…'
      : epdState.commentToStatus);

  const licenseeAdminReviewComment =
    epdState?.licenseeAdminReviewComment &&
    (epdState.licenseeAdminReviewComment.length > STATUS_COMMENT_MAX_LENGTH
      ? epdState.licenseeAdminReviewComment.slice(0, STATUS_COMMENT_MAX_LENGTH) + '…'
      : epdState.licenseeAdminReviewComment);

  return (
    <>
      <EpdStateContainer $isEPDDepublishedByAdminOrDeclined={isEPDDepublishedByAdminOrDeclined}>
        <EpdStateMessage>
          <b>
            <label>{t('epdWizard.epdState.title')}</label>
          </b>
          {statusMessage}
        </EpdStateMessage>
        <EpdStateMessage>
          <b>
            <label>{t('epdWizard.epdState.action.title')}</label>
          </b>
          {actionMessage}
        </EpdStateMessage>
      </EpdStateContainer>
      {epdState?.commentToStatus && isEPDDepublishedByAdminOrDeclined && (
        <>
          <EpdStateContainer $isEPDDepublishedByAdminOrDeclined={isEPDDepublishedByAdminOrDeclined}>
            <EpdStateMessage>
              <b>
                <label>
                  {epdState?.statusMessageCode === 'declinedVerification'
                    ? t('epdWizard.epdState.action.commentByVerifierTitle')
                    : t('epdWizard.epdState.action.commentBySecretariatTitle')}
                </label>
              </b>
              <InlineComment>
                {commentToStatus}{' '}
                {commentToStatus!.length > STATUS_COMMENT_MAX_LENGTH && (
                  <LinkButton onClick={() => setShowStatusCommentFullViewDialog(true)}>Read more</LinkButton>
                )}
              </InlineComment>
            </EpdStateMessage>
          </EpdStateContainer>
          <StatusCommentFullViewDialog
            headerTitle={
              epdState?.statusMessageCode === 'declinedVerification'
                ? t('epdWizard.epdState.action.commentByVerifierTitle')
                : t('epdWizard.epdState.action.commentBySecretariatTitle')
            }
            comment={epdState?.commentToStatus!}
            isOpened={showStatusCommentFullViewDialog}
            onClose={() => setShowStatusCommentFullViewDialog(false)}
          />
        </>
      )}
      {epdState?.statusMessageCode === 'beingReviewedBySecretariat' && licenseeAdminReviewComment && (
        <>
          <EpdStateContainer style={{ backgroundColor: '#F3F3F3' }}>
            <EpdStateMessage>
              <b>
                <label>{t('epdWizard.epdState.licenseeAdminReviewCommentTitle')}</label>
              </b>
              <InlineComment>
                {licenseeAdminReviewComment}{' '}
                {licenseeAdminReviewComment.length > STATUS_COMMENT_MAX_LENGTH && (
                  <LinkButton onClick={() => setShowStatusCommentFullViewDialog(true)}>Read more</LinkButton>
                )}
              </InlineComment>
            </EpdStateMessage>
          </EpdStateContainer>
          <StatusCommentFullViewDialog
            headerTitle={t('epdWizard.epdState.licenseeAdminReviewCommentTitle')}
            comment={epdState.licenseeAdminReviewComment!}
            isOpened={showStatusCommentFullViewDialog}
            onClose={() => setShowStatusCommentFullViewDialog(false)}
          />
        </>
      )}
    </>
  );
};

const EpdStateContainer = styled.div<{
  $isEPDDepublishedByAdminOrDeclined?: boolean;
}>`
  display: flex;
  flex-direction: row;
  background-color: ${(props) =>
    props?.$isEPDDepublishedByAdminOrDeclined
      ? props.theme.colors.statusLabelRedBgOldWizard
      : props.theme.colors.lightGreen};
  padding: 1rem;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const EpdStateMessage = styled.div`
  padding-right: 1rem;
  display: flex;
  flex-direction: column;

  label {
    font-style: normal;
    text-transform: uppercase;
    padding-bottom: 0.5rem;
    ${(props) => props.theme.fonts.textSmall}
  }
`;

const InlineComment = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${(props) => props.theme.colors.primaryBlack};
  cursor: pointer;
  font: inherit;
  text-decoration: underline;

  &:hover {
    color: ${(props) => props.theme.colors.primaryBlack};
  }
`;

export default StatusBox;
